<template>
  <transition name="toast-fade">
    <div
      v-if="show"
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div
        class="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
      ></div>
      <div
        class="bg-gray-800 text-white rounded-lg shadow-xl border border-gray-700 p-5 max-w-md z-10 transform transition-all duration-300"
        :class="[typeClasses, 'scale-100 opacity-100']"
      >
        <div class="flex items-start">
          <!-- Icons based on type -->
          <div class="flex-shrink-0 mr-4">
            <!-- Info icon -->
            <svg
              v-if="type === 'info'"
              class="h-8 w-8 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <!-- Success icon -->
            <svg
              v-else-if="type === 'success'"
              class="h-8 w-8 text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <!-- Warning icon -->
            <svg
              v-else-if="type === 'warning'"
              class="h-8 w-8 text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <!-- Error icon -->
            <svg
              v-else-if="type === 'error'"
              class="h-8 w-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <div class="flex-1">
            <div class="flex justify-between items-start">
              <h3 class="text-lg font-medium mb-2" :class="titleColorClass">
                {{ title || defaultTitle }}
              </h3>
              <button
                @click="closeToast"
                class="ml-4 text-gray-400 hover:text-gray-300 transition-colors"
              >
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <p class="text-base text-gray-300">{{ message }}</p>

            <div class="mt-4 flex justify-end" v-if="showAction">
              <button
                @click="handleAction"
                class="px-4 py-2 rounded-md text-sm font-medium transition-colors"
                :class="actionButtonClass"
              >
                {{ actionText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';

export default defineComponent({
  name: 'NotificationModal',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    message: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'info',
      validator: (value: string) => {
        return ['info', 'success', 'warning', 'error'].includes(value);
      },
    },
    duration: {
      type: Number,
      default: 1500, // Default duration in ms, 0 means no auto-close
    },
    showAction: {
      type: Boolean,
      default: false,
    },
    actionText: {
      type: String,
      default: 'OK',
    },
  },
  emits: ['close', 'action'],
  data() {
    return {
      timer: null as number | null,
    };
  },
  computed: {
    typeClasses(): object {
      return {
        'border-l-4 border-l-blue-500': this.type === 'info',
        'border-l-4 border-l-green-500': this.type === 'success',
        'border-l-4 border-l-yellow-500': this.type === 'warning',
        'border-l-4 border-l-red-500': this.type === 'error',
      };
    },
    titleColorClass(): string {
      switch (this.type) {
        case 'info':
          return 'text-blue-400';
        case 'success':
          return 'text-green-400';
        case 'warning':
          return 'text-yellow-400';
        case 'error':
          return 'text-red-400';
        default:
          return 'text-white';
      }
    },
    actionButtonClass(): object {
      switch (this.type) {
        case 'info':
          return { 'bg-blue-600 hover:bg-blue-700 text-white': true };
        case 'success':
          return { 'bg-green-600 hover:bg-green-700 text-white': true };
        case 'warning':
          return { 'bg-yellow-600 hover:bg-yellow-700 text-white': true };
        case 'error':
          return { 'bg-red-600 hover:bg-red-700 text-white': true };
        default:
          return { 'bg-gray-600 hover:bg-gray-700 text-white': true };
      }
    },
    defaultTitle(): string {
      switch (this.type) {
        case 'info':
          return 'Informacija';
        case 'success':
          return 'Uspešno';
        case 'warning':
          return 'Upozorenje';
        case 'error':
          return 'Greška';
        default:
          return 'Obaveštenje';
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.setTimer();
      } else if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    },
  },
  methods: {
    setTimer() {
      if (this.duration > 0) {
        this.timer = window.setTimeout(() => {
          this.closeToast();
        }, this.duration);
      }
    },
    closeToast() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
      this.$emit('close');
    },
    handleAction() {
      this.$emit('action');
      this.closeToast();
    },
  },
  mounted() {
    if (this.show) {
      this.setTimer();
    }
  },
  beforeUnmount() {
    if (this.timer) {
      window.clearTimeout(this.timer);
    }
  },
});
</script>

<style scoped>
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.25s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
}

.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}
</style>
